import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, Sheet } from "lucide-react";

const resultText = (r) => {
  if (!r) return "Not run yet";
  if (r.status === "error") return `Failed: ${r.error}`;
  if (r.status !== "ok") return r.status;
  return `${r.lines} notes in sheet · ${r.added} new entries${r.removed ? ` · ${r.removed} removed` : ""}${r.unmatched ? ` · ${r.unmatched} need matching` : ""}${r.pending ? ` · ${r.pending} to retry next hour` : ""}${r.rematched ? ` · ${r.rematched} newly matched` : ""}`;
};

export const MedSheetSync = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ url: "", enabled: true, start_year: new Date().getFullYear() });
  const { data } = useQuery({ queryKey: ["med-sheet"], queryFn: async () => (await api.get("/medications/sheet-settings")).data,
    refetchInterval: (q) => (q.state.data?.running ? 4000 : false) });
  const running = !!data?.running;

  useEffect(() => {
    if (data) setForm({ url: data.url || "", enabled: data.enabled, start_year: data.start_year || new Date().getFullYear() });
  }, [data?.url, data?.enabled, data?.start_year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data && !data.running) qc.invalidateQueries({ queryKey: ["medications"] });
  }, [data?.running, data?.last_run]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({ mutationFn: () => api.post("/medications/sheet-settings", { ...form, start_year: Number(form.start_year) }),
    onSuccess: () => { toast.success("Sheet settings saved"); qc.invalidateQueries({ queryKey: ["med-sheet"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save") });
  const sync = useMutation({ mutationFn: () => api.post("/medications/sheet-sync-now"),
    onSuccess: () => { toast.success("Sync started — new notes are read by AI, this can take a minute"); setTimeout(() => qc.invalidateQueries({ queryKey: ["med-sheet"] }), 800); } });

  return (
    <div data-testid="med-sheet-sync" className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-[#0B1F44]"><span className="inline-flex items-center gap-2"><Sheet className="h-4 w-4" />Google Sheet auto-sync</span><span className="font-normal text-slate-500">checked every hour</span></div>
      <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input data-testid="med-sheet-url" className="lg:flex-1" placeholder="Paste the Google Sheet link" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <label className="flex items-center gap-2 text-xs text-slate-600">Year of first note
          <Input data-testid="med-sheet-year" type="number" className="h-9 w-24" value={form.start_year} onChange={(e) => setForm({ ...form, start_year: e.target.value })} /></label>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="med-sheet-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />On</label>
        <Button variant="outline" data-testid="med-sheet-save" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
        <Button data-testid="med-sheet-sync-now" onClick={() => sync.mutate()} disabled={running || sync.isPending || !data?.url} className="bg-[#0B1F44] hover:bg-[#13306a]">
          <RefreshCw className={`mr-2 h-4 w-4 ${running ? "animate-spin" : ""}`} />{running ? "Syncing…" : "Sync now"}</Button>
      </div>
      <p data-testid="med-sheet-last" className={`mt-2 text-xs ${data?.last_result?.status === "error" ? "text-red-600" : "text-slate-500"}`}>
        {data?.last_run ? `Last run ${fmtDateTime(data.last_run)}: ` : ""}{resultText(data?.last_result)}</p>
      <p className="mt-1 text-xs text-slate-400">Each note's patient, medicine and dose are read by AI. Notes without a year count forward from the year above, moving to the next year after December.</p>
    </div>
  );
};
